import { Box, Stack } from "@mui/material";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useSettingsContext } from "../SettingsProvider";
import { OptionsManager } from "../components/OptionsManager";
import { SettingsPageHeader, FloatingSaveButton } from "../components";
import { RecordEditDialog } from "../components/RecordEditDialog";
import { optionSchema, optionDuplicateCriteria, OptionRecord } from "../components/schemas";
import { useRecordEditor } from "../hooks";

type VisitDetailsSettings = {
  typeOfVisitOptions: OptionRecord[];
  modeOfArrivalOptions: OptionRecord[];
  streamOptions: OptionRecord[];
  injuryIntentOptions: OptionRecord[];
  injuryCauseOptions: OptionRecord[];
  triageDirectionOptions: OptionRecord[];
  patientOriginOptions: OptionRecord[];
};

type OptionCategory = keyof VisitDetailsSettings;

// Category configuration for rendering
const CATEGORIES: { key: OptionCategory; title: string; emptyMessage: string }[] = [
  { key: "typeOfVisitOptions", title: "Type of Visit Options", emptyMessage: "No type of visit options configured." },
  { key: "modeOfArrivalOptions", title: "Mode of Arrival Options", emptyMessage: "No mode of arrival options configured." },
  { key: "streamOptions", title: "Stream Options", emptyMessage: "No stream options configured." },
  { key: "injuryIntentOptions", title: "Injury Intent Options", emptyMessage: "No injury intent options configured." },
  { key: "injuryCauseOptions", title: "Injury Cause Options", emptyMessage: "No injury cause options configured." },
  { key: "triageDirectionOptions", title: "Triage Directions Options", emptyMessage: "No triage direction options configured." },
  { key: "patientOriginOptions", title: "Patient Origin Options", emptyMessage: "No patient origin options configured." },
];

const DEFAULT_SETTINGS: VisitDetailsSettings = {
  typeOfVisitOptions: [],
  modeOfArrivalOptions: [],
  streamOptions: [],
  injuryIntentOptions: [],
  injuryCauseOptions: [],
  triageDirectionOptions: [],
  patientOriginOptions: [],
};

const VisitDetailsSettings = () => {
  const { settings, setSettings } = useSettingsContext();

  // Memoize original values
  const originalSettings = useMemo(() => {
    const visitDetails = (settings as any)?.visitDetails;
    return {
      typeOfVisitOptions: visitDetails?.typeOfVisitOptions ?? [],
      modeOfArrivalOptions: visitDetails?.modeOfArrivalOptions ?? [],
      streamOptions: visitDetails?.streamOptions ?? [],
      injuryIntentOptions: visitDetails?.injuryIntentOptions ?? [],
      injuryCauseOptions: visitDetails?.injuryCauseOptions ?? [],
      triageDirectionOptions: visitDetails?.triageDirectionOptions ?? [],
      patientOriginOptions: visitDetails?.patientOriginOptions ?? [],
    } as VisitDetailsSettings;
  }, [settings]);

  // Local state - single object for all categories
  const [localSettings, setLocalSettings] = useState<VisitDetailsSettings>(DEFAULT_SETTINGS);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize local state when settings change
  useEffect(() => {
    setLocalSettings(originalSettings);
    setIsInitialized(true);
  }, [originalSettings]);

  // Calculate if dirty
  const isDirty = useMemo(() => {
    if (!isInitialized) return false;
    return JSON.stringify(localSettings) !== JSON.stringify(originalSettings);
  }, [localSettings, originalSettings, isInitialized]);

  // Create setter for a category
  const createCategorySetter = useCallback((category: OptionCategory) => {
    return (valueOrUpdater: OptionRecord[] | ((prev: OptionRecord[]) => OptionRecord[])) => {
      setLocalSettings((prev) => ({
        ...prev,
        [category]: typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev[category])
          : valueOrUpdater,
      }));
    };
  }, []);

  // Create record editors for each category
  const editors = {
    typeOfVisitOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("typeOfVisitOptions"),
      displayField: "code",
    }),
    modeOfArrivalOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("modeOfArrivalOptions"),
      displayField: "code",
    }),
    streamOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("streamOptions"),
      displayField: "code",
    }),
    injuryIntentOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("injuryIntentOptions"),
      displayField: "code",
    }),
    injuryCauseOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("injuryCauseOptions"),
      displayField: "code",
    }),
    triageDirectionOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("triageDirectionOptions"),
      displayField: "code",
    }),
    patientOriginOptions: useRecordEditor<OptionRecord>({
      setRecords: createCategorySetter("patientOriginOptions"),
      displayField: "code",
    }),
  };

  // Find which editor has an active editing record
  const activeEditor = CATEGORIES.find(({ key }) => editors[key].editingRecord !== null);
  const editingRecord = activeEditor ? editors[activeEditor.key].editingRecord : null;

  const handleEditCancel = useCallback(() => {
    Object.values(editors).forEach((editor) => editor.handleEditCancel());
  }, [editors]);

  const handleEditSave = useCallback((record: OptionRecord) => {
    if (activeEditor) {
      editors[activeEditor.key].handleEditSave(record);
    }
  }, [activeEditor, editors]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await setSettings({ visitDetails: localSettings });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }}>
      <Box sx={{ height: "100%", width: "100%", overflow: "auto", p: 3, pb: 10 }}>
        <Stack spacing={4} sx={{ width: "100%" }}>
          <SettingsPageHeader title="Visit Details Settings" isDirty={isDirty} />

          {CATEGORIES.map(({ key, title, emptyMessage }) => (
            <OptionsManager
              key={key}
              title={title}
              records={localSettings[key]}
              onChange={createCategorySetter(key)}
              schemaDefinition={optionSchema}
              duplicateRecordCriteria={optionDuplicateCriteria}
              emptyMessage={`${emptyMessage} Click "Add" or "Import CSV" to get started.`}
              onEdit={editors[key].handleEdit}
              onDelete={editors[key].handleDelete}
            />
          ))}
        </Stack>

        <RecordEditDialog
          open={editingRecord !== null}
          onClose={handleEditCancel}
          onSave={handleEditSave}
          record={editingRecord}
          schemaDefinition={optionSchema}
        />

        <FloatingSaveButton show={isDirty} onSave={handleSave} isSaving={isSaving} />
      </Box>
    </Box>
  );
};

export { VisitDetailsSettings };
